import type { SpaceEntry } from './types.js'

const COUNTER_MAX = (36 ** 4) - 1
const SESSION_NODE = randomSegment()
const STRUCTURED_ID_RE = /^\d{4} \d{4} \d{4} \d{4} [0-9A-Z]{4} [0-9A-Z]{4}$/
const ZERO_TIME = '0000 0000 0000 0000'

export type StructuredIdDate = {
  year: string
  month: string
  day: string
}

export function parseStructuredIdDate(id: string): StructuredIdDate | undefined {
  if (!STRUCTURED_ID_RE.test(id)) return undefined
  const year = id.slice(0, 4)
  const month = id.slice(5, 7)
  const day = id.slice(7, 9)
  const parsed = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day)))
  if (
    parsed.getUTCFullYear() !== Number(year)
    || parsed.getUTCMonth() + 1 !== Number(month)
    || parsed.getUTCDate() !== Number(day)
  ) return undefined
  return { year, month, day }
}

let clockTime = ZERO_TIME
let clockCounter = 0

export const generateId = (): string => {
  const wallTime = formatUtcStamp(new Date())

  if (wallTime > clockTime) {
    clockTime = wallTime
    clockCounter = 1
  } else {
    if (clockCounter >= COUNTER_MAX) {
      clockTime = addCentisecond(clockTime)
      clockCounter = 1
    } else {
      clockCounter += 1
    }
  }

  return `${clockTime} ${toBase36(clockCounter)} ${SESSION_NODE}`
}

export function seedClock(blocks: SpaceEntry[]): void {
  // Legacy local-time stamps have no offset: preserve them as ordering floors.
  let maxTime = clockTime
  let maxCounter = clockCounter

  for (const block of blocks) {
    considerId(block.ided[0])
    considerId(block.ided[1])
  }

  clockTime = maxTime
  clockCounter = maxCounter

  function considerId(id: string): void {
    if (!STRUCTURED_ID_RE.test(id)) return
    const time = id.slice(0, 19)
    const counter = parseInt(id.slice(20, 24), 36)
    if (!Number.isFinite(counter)) return
    if (time > maxTime || (time === maxTime && counter > maxCounter)) {
      maxTime = time
      maxCounter = counter
    }
  }
}

export function _resetClock(): void {
  clockTime = ZERO_TIME
  clockCounter = 0
}

// Authored date/time snippets keep local time; HLC stamps use UTC below.
export function formatDateStamp(now: Date): string {
  const year = now.getFullYear()
  const month = String(now.getMonth() + 1).padStart(2, '0')
  const day = String(now.getDate()).padStart(2, '0')
  return `${year} ${month}${day}`
}

export function formatTimeStamp(now: Date): string {
  const hour = String(now.getHours()).padStart(2, '0')
  const minute = String(now.getMinutes()).padStart(2, '0')
  return `${hour}${minute}`
}

function formatUtcStamp(now: Date): string {
  const year = now.getUTCFullYear()
  const month = String(now.getUTCMonth() + 1).padStart(2, '0')
  const day = String(now.getUTCDate()).padStart(2, '0')
  const hour = String(now.getUTCHours()).padStart(2, '0')
  const minute = String(now.getUTCMinutes()).padStart(2, '0')
  const second = String(now.getUTCSeconds()).padStart(2, '0')
  const centisecond = String(Math.floor(now.getUTCMilliseconds() / 10)).padStart(2, '0')
  return `${year} ${month}${day} ${hour}${minute} ${second}${centisecond}`
}

function addCentisecond(time: string): string {
  if (!STRUCTURED_TIME_RE.test(time)) {
    return ZERO_TIME
  }

  const year = Number(time.slice(0, 4))
  const month = Number(time.slice(5, 7))
  const day = Number(time.slice(7, 9))
  const hour = Number(time.slice(10, 12))
  const minute = Number(time.slice(12, 14))
  const second = Number(time.slice(15, 17))
  const centisecond = Number(time.slice(17, 19))

  const next = new Date(Date.UTC(year, month - 1, day, hour, minute, second, centisecond * 10 + 10))
  return formatUtcStamp(next)
}

function toBase36(value: number): string {
  return value.toString(36).toUpperCase().padStart(4, '0')
}

function randomSegment(): string {
  const value = Math.floor(Math.random() * 36 ** 4)
  return toBase36(value)
}

const STRUCTURED_TIME_RE = /^\d{4} \d{4} \d{4} \d{4}$/
